import {
  createNavMembershipClicked,
  createStoryViewedPageView,
  createUtilityFAQClicked,
  createTLPViewedPageView,
  createPDPViewedPageView,
  createBuyNowClickedEvent,
  createUtilityViewedPageView,
  createDetailsViewedPageView,
  createTokenClickedEvent,
  createTokenAssetViewedView,
  createPDPErrorViewedView,
  createTransactionPendingPageView,
  createCheckoutCompletedEvent,
  createCheckoutStartedEvent,
  createGotEmViewedPageView,
  createRevealViewedView,
  createTokenRevealedEvent,
  createReveaClickedEvent,
  createTokenFavoritedTLPEvent,
  createTokenFavoritedCLPEvent,
  createCLPViewedView,
  createViewDetailsReveaClickedEvent,
  createReplayReveaClickedEvent,
} from "./analyticsEventsCreators";
import { useAnalyticsContext } from "./AnalyticsProvider";
import { trackEvent, trackPageView } from "./useAnalytics";
import { useProductData } from "@/src/hooks/useProductData";
import { useTokenMetadata } from "@/src/hooks/useTokenMetadata";
import { useParams } from "next/navigation";
import { Product } from "nvs-types/dist/types/generated/contentful";
import { useCollectionData } from "@/src/hooks/useCollectionData";
import { useEffect, useState } from "react";
import {
  NFTContract,
  OpenSeaMetaData,
} from "@/src/routes/profile/routes/[handle]/types";
import { getProductPurchaseInfo } from "@/src/helpers/getProductPurchaseInfo";
import { MINT_CHAIN_ID } from "../apis/onboarding/minting/constants";
import { TokenInformationType } from "@/src/hooks/useTokenInformation/types";
import { TokenHitResult } from "@/src/routes/gallery/routes/collections/routes/[collectionName]/components/CollectionTokenListings/types";
import type { IndexUiState } from "instantsearch.js";
import { OrderProperties } from "./types";

const activeUtilityCount = (productData: Product | undefined) => {
  let count = 0;
  productData?.tabs?.utilityTab?.badgesCollection?.items.map((i) => {
    if (i?.active) {
      count++;
    }
  });
  return count;
};

// --------------------- TRACK EVENTS -----------------------

export const useCreateNavMembershipClickedEvent = () => {
  const analyticsContext = useAnalyticsContext();
  return () => {
    analyticsContext?.setPreviousView(
      trackEvent(
        createNavMembershipClicked(
          analyticsContext?.currentUser,
          analyticsContext?.previousView,
          analyticsContext?.country,
        ),
      ),
    );
  };
};

export const useCreateUtilityFAQClickedAnalytics = () => {
  const params = useParams();
  const productName = params?.productName;
  const collectionName = params?.collectionName;
  const tokenId = params?.tokenId;

  const { productData } = useProductData(productName as string);
  const { data: tokenData } = useTokenMetadata(
    tokenId as string,
    collectionName as string,
    productName as string,
  );
  const analyticsContext = useAnalyticsContext();
  return () => {
    analyticsContext?.setPreviousView(
      trackPageView(
        createUtilityFAQClicked(
          analyticsContext?.currentUser,
          analyticsContext?.previousView,
          analyticsContext?.country,
          {
            token: {
              tokenId,
              name: tokenData?.name,
              utility: productData?.tabs?.utilityTab?.badgesCollection?.items,
              activeUtilityCount: activeUtilityCount(productData),
              ownerHandle: tokenData?.ownedBy,
              //TODO: get data when available
              // isListed: tokenData?.isListed,
              // favorites: tokenData?.favorites,
            },
          },
        ),
      ),
    );
  };
};

export const useCreateTokenClickedEvent = (
  collectionName: string,
  nft: any,
  pageType: string,
) => {
  const analyticsContext = useAnalyticsContext();
  return () => {
    analyticsContext?.setPreviousView(
      trackEvent(
        createTokenClickedEvent(
          analyticsContext?.currentUser,
          analyticsContext?.previousView,
          analyticsContext?.country,
          {
            token: {
              tokenId: nft.tokenId,
              name: nft.metadata.name,
              ownerHandle: nft.metadata.ownedBy,
              isListed: false,
              favorites: nft.favoriteCount,
            },
          },
          {
            collection: {
              name: collectionName,
              contractAddress: nft?.contractAddress,
              chainId: parseInt(MINT_CHAIN_ID),
              slug: collectionName,
            },
          },
          pageType,
        ),
      ),
    );
  };
};

export const useCreateTokenAssetViewedViewAnalytics = () => {
  const params = useParams();
  const productName = params?.productName;
  const collectionName = params?.collectionName;
  const tokenId = params?.tokenId;

  const { collectionData } = useCollectionData(collectionName as string);
  const { productData } = useProductData(productName as string);
  const { data: tokenData } = useTokenMetadata(
    tokenId as string,
    collectionName as string,
    productName as string,
  );
  const analyticsContext = useAnalyticsContext();
  return () => {
    analyticsContext?.setPreviousView(
      trackPageView(
        createTokenAssetViewedView(
          analyticsContext?.currentUser,
          analyticsContext?.previousView,
          analyticsContext?.country,
          {
            token: {
              tokenId,
              name: tokenData?.name,
              utility: productData?.tabs?.utilityTab?.badgesCollection?.items,
              activeUtilityCount: activeUtilityCount(productData),
              ownerHandle: tokenData?.ownedBy,
              //TODO: get data when available
              // isListed: tokenData?.isListed,
              // favorites: tokenData?.favorites,
            },
          },
          {
            collection: {
              name: collectionData?.name,
              contractAddress: collectionData?.contractAddress,
              chainId: collectionData?.chainId,
              slug: collectionData?.slug,
            },
          },
        ),
      ),
    );
  };
};

export const useCreateBuyNowClickedAnalytics = () => {
  const analyticsContext = useAnalyticsContext();

  return (product: Product) => {
    analyticsContext?.setPreviousView(
      trackEvent(
        createBuyNowClickedEvent(
          analyticsContext?.currentUser,
          analyticsContext?.previousView,
          analyticsContext?.country,
          { launchType: "Buy" },
          {
            price: parseFloat(product.primarySalePrice as string),
            productType: "Virtual",
            name: product.name,
            productId: product.sys.id,
          },
        ),
      ),
    );
  };
};

export const useCreateCheckoutStartedAnalytics = () => {
  const analyticsContext = useAnalyticsContext();
  const [isCaptured, setAnalyticsCaptured] = useState(false);

  const params = useParams();
  const productName = params?.productName;

  const { productData: product, productInventory } = useProductData(
    productName as string,
  );

  const purchaseInfo =
    product &&
    productInventory &&
    getProductPurchaseInfo(product, productInventory);

  const capture = (orderId: string) => {
    const view =
      !!purchaseInfo &&
      !!analyticsContext &&
      trackEvent(
        createCheckoutStartedEvent(
          analyticsContext?.currentUser,
          analyticsContext?.previousView,
          analyticsContext?.country,
          { orderType: purchaseInfo.sale.title, orderId },
          { launchType: "Sale" },
          {
            price: parseFloat(product.primarySalePrice as string),
            productType: "Virtual",
            name: product.name,
            productId: product.sys.id,
          },
        ),
      );

    if (!!view) {
      setAnalyticsCaptured(true);
      analyticsContext?.setPreviousView(view);
    }
  };

  return { capture, isCaptured };
};

export const useCreateCheckoutCompletedAnalytics = () => {
  const analyticsContext = useAnalyticsContext();

  return (order: OrderProperties, product: Product) => {
    const view = trackEvent(
      createCheckoutCompletedEvent(
        analyticsContext?.currentUser,
        analyticsContext?.previousView,
        analyticsContext?.country,
        order,
        { launchType: "Sale" },
        {
          price: parseFloat(product.primarySalePrice as string),
          productType: "Virtual",
          name: product.name,
          productId: product.sys.id,
        },
      ),
    );

    if (!!view) {
      analyticsContext?.setPreviousView(view);
    }
  };
};

// --------------------- PAGE VIEWS -------------------

export const usePDPViewedPageViewAnalytics = () => {
  const params = useParams();
  const productName = params?.productName;

  const { productData } = useProductData(productName as string);

  const analyticsContext = useAnalyticsContext();
  const [analyticsCaptured, setAnalyticsCaptured] = useState(false);

  useEffect(() => {
    // If we have already triggered an analytics event and the hook re-renders just return
    if (analyticsCaptured) return;

    // Check we have the data
    if (analyticsContext !== undefined && productData !== undefined) {
      analyticsContext?.setPreviousView(
        trackPageView(
          createPDPViewedPageView(
            analyticsContext?.currentUser,
            analyticsContext?.previousView,
            analyticsContext?.country,
            { launchType: "Buy" },
            {
              price: parseFloat(productData.primarySalePrice as string),
              productType: "Virtual",
              name: productData.name,
              productId: productData.sys.id,
            },
          ),
        ),
      );
      setAnalyticsCaptured(true);
    }
  }, [analyticsContext, productData, analyticsCaptured]);
};

export const useTLPViewedPageViewAnalytics = () => {
  const params = useParams();
  const productName = params?.productName;
  const collectionName = params?.collectionName;
  const tokenId = params?.tokenId;

  const { collectionData } = useCollectionData(collectionName as string);
  const { productData } = useProductData(productName as string);
  const { data: tokenData } = useTokenMetadata(
    tokenId as string,
    collectionName as string,
    productName as string,
  );
  const analyticsContext = useAnalyticsContext();
  const [analyticsCaptured, setAnalyticsCaptured] = useState(false);

  useEffect(() => {
    // If we have already triggered an analytics event and the hook re-renders just return
    if (analyticsCaptured) return;

    // Check we have the data
    if (
      analyticsContext !== undefined &&
      tokenId !== undefined &&
      collectionData !== undefined &&
      productData !== undefined &&
      tokenData !== undefined
    ) {
      analyticsContext?.setPreviousView(
        trackPageView(
          createTLPViewedPageView(
            analyticsContext?.currentUser,
            analyticsContext?.previousView,
            analyticsContext?.country,
            {
              token: {
                tokenId,
                name: tokenData?.name,
                utility: productData?.tabs?.utilityTab?.badgesCollection?.items,
                activeUtilityCount: activeUtilityCount(productData),
                ownerHandle: tokenData?.ownedBy,
                //TODO: get data when available
                // isListed: tokenData?.isListed,
                // favorites: tokenData?.favorites,
              },
            },
            {
              collection: {
                name: collectionData?.name,
                contractAddress: collectionData?.contractAddress,
                chainId: collectionData?.chainId,
                slug: collectionData?.slug,
              },
            },
          ),
        ),
      );
      setAnalyticsCaptured(true);
    }
  }, [tokenId, collectionData, productData, tokenData, analyticsCaptured]);
};

export const useStoryViewedPageViewAnalytics = () => {
  const params = useParams();
  const productName = params?.productName;
  const collectionName = params?.collectionName;
  const tokenId = params?.tokenId;

  const { collectionData } = useCollectionData(collectionName as string);
  const { productData } = useProductData(productName as string);
  const { data: tokenData } = useTokenMetadata(
    tokenId as string,
    collectionName as string,
    productName as string,
  );
  const analyticsContext = useAnalyticsContext();
  const [analyticsCaptured, setAnalyticsCaptured] = useState(false);

  useEffect(() => {
    // If we have already triggered an analytics event and the hook re-renders just return
    if (analyticsCaptured) return;

    // Check we have the data
    if (
      analyticsContext !== undefined &&
      tokenId !== undefined &&
      collectionData !== undefined &&
      productData !== undefined &&
      tokenData !== undefined
    ) {
      analyticsContext?.setPreviousView(
        trackPageView(
          createStoryViewedPageView(
            analyticsContext?.currentUser,
            analyticsContext?.previousView,
            analyticsContext?.country,
            {
              token: {
                tokenId,
                name: tokenData?.name,
                utility: productData?.tabs?.utilityTab?.badgesCollection?.items,
                activeUtilityCount: activeUtilityCount(productData),
                ownerHandle: tokenData?.ownedBy,
                //TODO: get data when available
                // isListed: tokenData?.isListed,
                // favorites: tokenData?.favorites,
              },
            },
            {
              collection: {
                name: collectionData?.name,
                contractAddress: collectionData?.contractAddress,
                chainId: collectionData?.chainId,
                slug: collectionData?.slug,
              },
            },
          ),
        ),
      );
      setAnalyticsCaptured(true);
    }
  }, [tokenId, collectionData, productData, tokenData, analyticsCaptured]);
};

export const useUtilityViewedPageViewAnalytics = () => {
  const params = useParams();
  const productName = params?.productName;
  const collectionName = params?.collectionName;
  const tokenId = params?.tokenId;

  const { collectionData } = useCollectionData(collectionName as string);
  const { productData } = useProductData(productName as string);
  const { data: tokenData } = useTokenMetadata(
    tokenId as string,
    collectionName as string,
    productName as string,
  );
  const analyticsContext = useAnalyticsContext();

  const [analyticsCaptured, setAnalyticsCaptured] = useState(false);

  useEffect(() => {
    // If we have already triggered an analytics event and the hook re-renders just return
    if (analyticsCaptured) return;

    // Check we have the data
    if (
      analyticsContext !== undefined &&
      tokenId !== undefined &&
      collectionData !== undefined &&
      productData !== undefined &&
      tokenData !== undefined
    ) {
      analyticsContext?.setPreviousView(
        trackPageView(
          createUtilityViewedPageView(
            analyticsContext?.currentUser,
            analyticsContext?.previousView,
            analyticsContext?.country,
            {
              token: {
                tokenId,
                name: tokenData?.name,
                utility: productData?.tabs?.utilityTab?.badgesCollection?.items,
                activeUtilityCount: activeUtilityCount(productData),
                ownerHandle: tokenData?.ownedBy,
                //TODO: get data when available
                // isListed: tokenData?.isListed,
                // favorites: tokenData?.favorites,
              },
            },
            {
              collection: {
                name: collectionData?.name,
                contractAddress: collectionData?.contractAddress,
                chainId: collectionData?.chainId,
                slug: collectionData?.slug,
              },
            },
          ),
        ),
      );
      setAnalyticsCaptured(true);
    }
  }, [tokenId, collectionData, productData, tokenData, analyticsCaptured]);
};

export const useDetailsViewedPageViewAnalytics = () => {
  const params = useParams();
  const productName = params?.productName;
  const collectionName = params?.collectionName;
  const tokenId = params?.tokenId;

  const { collectionData } = useCollectionData(collectionName as string);
  const { productData } = useProductData(productName as string);
  const { data: tokenData } = useTokenMetadata(
    tokenId as string,
    collectionName as string,
    productName as string,
  );
  const analyticsContext = useAnalyticsContext();
  const [analyticsCaptured, setAnalyticsCaptured] = useState(false);

  useEffect(() => {
    // If we have already triggered an analytics event and the hook re-renders just return
    if (analyticsCaptured) return;

    // Check we have the data
    if (
      analyticsContext !== undefined &&
      tokenId !== undefined &&
      collectionData !== undefined &&
      productData !== undefined &&
      tokenData !== undefined
    ) {
      analyticsContext?.setPreviousView(
        trackPageView(
          createDetailsViewedPageView(
            analyticsContext?.currentUser,
            analyticsContext?.previousView,
            analyticsContext?.country,
            {
              token: {
                tokenId,
                name: tokenData?.name,
                utility: productData?.tabs?.utilityTab?.badgesCollection?.items,
                activeUtilityCount: activeUtilityCount(productData),
                ownerHandle: tokenData?.ownedBy,
                //TODO: get data when available
                // isListed: tokenData?.isListed,
                // favorites: tokenData?.favorites,
              },
            },
            {
              collection: {
                name: collectionData?.name,
                contractAddress: collectionData?.contractAddress,
                chainId: collectionData?.chainId,
                slug: collectionData?.slug,
              },
            },
          ),
        ),
      );
      setAnalyticsCaptured(true);
    }
  }, [tokenId, collectionData, productData, tokenData, analyticsCaptured]);
};

export const useCreatePDPErrorViewedPageViewAnalytics = () => {
  const analyticsContext = useAnalyticsContext();

  return (message: { title: string; body: string }) => {
    analyticsContext?.setPreviousView(
      trackPageView(
        createPDPErrorViewedView(
          analyticsContext?.currentUser,
          analyticsContext?.previousView,
          analyticsContext?.country,
          message,
        ),
      ),
    );
  };
};

export const useTransactionPendingPageViewAnalytics = () => {
  const analyticsContext = useAnalyticsContext();

  return (order: OrderProperties, product: Product) => {
    const view = trackPageView(
      createTransactionPendingPageView(
        analyticsContext?.currentUser,
        analyticsContext?.previousView,
        analyticsContext?.country,
        order,
        { launchType: "Sale" },
        {
          price: parseFloat(product.primarySalePrice as string),
          productType: "Virtual",
          name: product.name,
          productId: product.sys.id,
        },
      ),
    );

    if (!!view) {
      analyticsContext?.setPreviousView(view);
    }
  };
};

export const useGotEmViewedPageViewAnalytics = () => {
  const analyticsContext = useAnalyticsContext();

  return (order: OrderProperties, product: Product) => {
    const view = trackPageView(
      createGotEmViewedPageView(
        analyticsContext?.currentUser,
        analyticsContext?.previousView,
        analyticsContext?.country,
        order,
        { launchType: "Sale" },
        {
          price: parseFloat(product.primarySalePrice as string),
          productType: "Virtual",
          name: product.name,
          productId: product.sys.id,
        },
      ),
    );

    if (!!view) {
      analyticsContext?.setPreviousView(view);
    }
  };
};

export const useRevealViewedPageViewAnalytics = () => {
  const params = useParams();
  const collectionName = params?.collectionName;
  const { collectionData } = useCollectionData(collectionName as string);
  const analyticsContext = useAnalyticsContext();
  const [analyticsCaptured, setAnalyticsCaptured] = useState(false);

  useEffect(() => {
    // If we have already triggered an analytics event and the hook re-renders just return
    if (analyticsCaptured) return;
    // Check we have the data
    if (analyticsContext !== undefined && collectionData !== undefined) {
      analyticsContext?.setPreviousView(
        trackPageView(
          createRevealViewedView(
            analyticsContext?.currentUser,
            analyticsContext?.previousView,
            analyticsContext?.country,
            {
              launchType: "Reveal",
            },
            {
              name: collectionData?.name,
              contractAddress: collectionData?.contractAddress,
              chainId: collectionData?.chainId,
              slug: collectionData?.slug,
            },
          ),
        ),
      );
      setAnalyticsCaptured(true);
    }
  }, [collectionData, analyticsCaptured]);
};

export const useCreateTokenRevealedViewAnalytics = () => {
  const analyticsContext = useAnalyticsContext();
  return (tokenData?: NFTContract) => {
    analyticsContext?.setPreviousView(
      trackEvent(
        createTokenRevealedEvent(
          analyticsContext?.currentUser,
          analyticsContext?.previousView,
          analyticsContext?.country,
          {
            tokenId: tokenData?.tokenId,
            name: tokenData?.metadata.name,
            // we shouldn't also be fetching the favorite data here - as soon a product is revealed
            // the favorite count should be 0
            ownerHandle: tokenData?.metadata.ownedBy,
            isListed: false,
            favorites: 0,
          },
          {
            name: tokenData?.collectionSlug,
            contractAddress: tokenData?.contractAddress,
            chainId: parseInt(MINT_CHAIN_ID), // chain Id does not change per env
            slug: tokenData?.collectionSlug,
          },
        ),
      ),
    );
  };
};

export const useCreateRevealClickedViewAnalytics = (
  tokenId: string,
  metadata: OpenSeaMetaData,
  contractAddress: string,
  collectionSlug: string,
) => {
  const analyticsContext = useAnalyticsContext();
  return () => {
    analyticsContext?.setPreviousView(
      trackEvent(
        createReveaClickedEvent(
          analyticsContext?.currentUser,
          analyticsContext?.previousView,
          analyticsContext?.country,
          {
            tokenId,
            name: metadata.name,
            ownerHandle: metadata.ownedBy,
            isListed: false, // TODO: Update this when we have data coming from marketplace
          },
          {
            name: collectionSlug,
            contractAddress: contractAddress,
            chainId: parseInt(MINT_CHAIN_ID), // chain Id does not change per env
            slug: collectionSlug,
          },
          {
            launchType: "Reveal",
          },
        ),
      ),
    );
  };
};

export const useCreateTokenFavoritedTLPAnalytics = (
  tokenId: string,
  contractAddress: string,
  collectionSlug: string,
  tokenInfo: TokenInformationType | undefined,
  tokenName: string,
  swooshHandle: string,
) => {
  const analyticsContext = useAnalyticsContext();
  return () => {
    analyticsContext?.setPreviousView(
      trackEvent(
        createTokenFavoritedTLPEvent(
          analyticsContext?.currentUser,
          analyticsContext?.previousView,
          analyticsContext?.country,
          {
            favorites: tokenInfo?.favoriteCount,
            isListed: false, // TODO: Update this when we have data coming from marketplace
            name: tokenName,
            ownerHandle: swooshHandle,
            tokenId,
          },
          {
            name: collectionSlug,
            contractAddress: contractAddress,
            chainId: parseInt(MINT_CHAIN_ID), // chain Id does not change per env
            slug: collectionSlug,
          },
        ),
      ),
    );
  };
};

export const useCreateTokenFavoritedCLPAnalytics = (
  searchState: IndexUiState,
) => {
  const analyticsContext = useAnalyticsContext();

  return (nft: TokenHitResult) => {
    analyticsContext?.setPreviousView(
      trackEvent(
        createTokenFavoritedCLPEvent(
          analyticsContext?.currentUser,
          analyticsContext?.previousView,
          analyticsContext?.country,
          {
            favorites: nft.favoriteCount,
            isListed: false, // TODO: Update this when we have data coming from marketplace
            name: nft.metadata.name,
            ownerHandle: nft.metadata.ownedBy,
            tokenId: nft.tokenId,
          },
          {
            chainId: parseInt(MINT_CHAIN_ID), // chain Id does not change per env
            contractAddress: nft.contractAddress,
            name: nft.collectionSlug,
            slug: nft.collectionSlug,
          },
          {
            sortBy: (searchState.sortBy && searchState.sortBy) || "",
          },
          {
            appliedFilters: searchState.refinementList,
          },
        ),
      ),
    );
  };
};

export const useCLPViewedPageViewAnalytics = (sortBy: string | undefined) => {
  const params = useParams();
  const collectionName = params?.collectionName;
  const { collectionData } = useCollectionData(collectionName as string);
  const analyticsContext = useAnalyticsContext();
  const [analyticsCaptured, setAnalyticsCaptured] = useState(false);

  useEffect(() => {
    // If we have already triggered an analytics event and the hook re-renders just return
    if (analyticsCaptured) return;

    // Check we have the data
    if (analyticsContext !== undefined && collectionData !== undefined) {
      analyticsContext?.setPreviousView(
        trackPageView(
          createCLPViewedView(
            analyticsContext?.currentUser,
            analyticsContext?.previousView,
            analyticsContext?.country,
            {
              name: collectionData?.name,
              contractAddress: collectionData?.contractAddress,
              chainId: collectionData?.chainId,
              slug: collectionData?.slug,
            },
            {
              sortBy: (sortBy && sortBy) || "",
            },
          ),
        ),
      );
      setAnalyticsCaptured(true);
    }
  }, [collectionData, analyticsCaptured]);
};

export const useCreateViewDetailsRevealClickedViewAnalytics = () => {
  const analyticsContext = useAnalyticsContext();
  return (
    tokenId: string,
    collectionSlug: string,
    metadata?: OpenSeaMetaData,
    contractAddress?: string,
  ) => {
    analyticsContext?.setPreviousView(
      trackEvent(
        createViewDetailsReveaClickedEvent(
          analyticsContext?.currentUser,
          analyticsContext?.previousView,
          analyticsContext?.country,
          {
            tokenId,
            name: metadata?.name,
            ownerHandle: metadata?.ownedBy,
            isListed: false, // TODO: Update this when we have data coming from marketplace
          },
          {
            name: collectionSlug,
            contractAddress: contractAddress,
            chainId: parseInt(MINT_CHAIN_ID), // chain Id does not change per env
            slug: collectionSlug,
          },
          {
            launchType: "Reveal",
            launchStatus: "active",
          },
        ),
      ),
    );
  };
};

export const useCreateReplayRevealClickedViewAnalytics = () => {
  const analyticsContext = useAnalyticsContext();
  return (
    tokenId: string,
    collectionSlug: string,
    metadata?: OpenSeaMetaData,
    contractAddress?: string,
  ) => {
    analyticsContext?.setPreviousView(
      trackEvent(
        createReplayReveaClickedEvent(
          analyticsContext?.currentUser,
          analyticsContext?.previousView,
          analyticsContext?.country,
          {
            tokenId,
            name: metadata?.name,
            ownerHandle: metadata?.ownedBy,
            isListed: false, // TODO: Update this when we have data coming from marketplace
          },
          {
            name: collectionSlug,
            contractAddress: contractAddress,
            chainId: parseInt(MINT_CHAIN_ID), // chain Id does not change per env
            slug: collectionSlug,
          },
          {
            launchType: "Reveal",
            launchStatus: "active",
          },
        ),
      ),
    );
  };
};
